import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import axios from 'utils/axios';

const WORKORDER_NOTIFICATION_POLL_MS = 30000;

export function isWorkOrdersRoute(pathname = '') {
    return pathname === '/workorders' || pathname.startsWith('/workorders/');
}

export function shouldNotifyUnreadIncrease(previousCount, nextCount) {
    return Number.isFinite(previousCount) && Number.isFinite(nextCount) && nextCount > previousCount;
}

export function notificationRuntimePreferences(prefs = {}) {
    return {
        soundEnabled: prefs.sound_enabled ?? true,
        browserNotificationsEnabled: prefs.browser_notifications_enabled ?? true
    };
}

export function buildNotificationListPath(filter = 'all') {
    const params = new URLSearchParams({
        filter,
        limit: '50',
    });
    return `/v1/notifications?${params}`;
}

export function buildUnreadCountPath() {
    return '/v1/notifications/unread-count';
}

export function buildMarkAllReadPath() {
    return '/v1/notifications/read-all';
}

export function nextFilterAfterMarkAllRead(filter = 'all') {
    return filter === 'unread' ? 'all' : filter;
}

export function notificationActorId(user) {
    return user?.id ?? null;
}

function playNotificationDing() {
    if (typeof window === 'undefined') return;
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return;

    try {
        const audioContext = new AudioContextCtor();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.001, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.18);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2);
        oscillator.onended = () => audioContext.close();
    } catch (err) {
        console.debug('Notification sound unavailable:', err);
    }
}

function showBrowserNotification(delta) {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (window.Notification.permission !== 'granted') return;

    const body = delta === 1 ? '1 new work notification' : `${delta} new work notifications`;
    new window.Notification('New work available', { body });
}

/**
 * useNotifications - Hook for fetching and managing notifications.
 * 
 * Connects to event_orchestrator notification log on Pi via BFF proxy.
 */
export default function useNotifications() {
    const { user } = useAuth();
    const location = useLocation();
    const userId = notificationActorId(user);
    const runtimePrefs = notificationRuntimePreferences(user?.notification_preferences);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all'); // all, unread, outbound, inbound
    const unreadCountRef = useRef(0);
    const hasUnreadBaselineRef = useRef(false);

    // Fetch notifications
    const fetchNotifications = useCallback(async () => {
        if (!userId) {
            setNotifications([]);
            setUnreadCount(0);
            unreadCountRef.current = 0;
            hasUnreadBaselineRef.current = false;
            setTotalCount(0);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Call event_orchestrator notification API via BFF proxy
            const response = await axios.get(buildNotificationListPath(filter));
            const data = response.data;

            setNotifications(data.notifications || []);
            const nextUnreadCount = data.unread_count || 0;
            setUnreadCount(nextUnreadCount);
            unreadCountRef.current = nextUnreadCount;
            hasUnreadBaselineRef.current = true;
            setTotalCount(data.total_count || data.notifications?.length || 0);
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [filter, userId]);

    const fetchUnreadCount = useCallback(async () => {
        if (!userId) return;

        try {
            const response = await axios.get(buildUnreadCountPath());
            const nextUnreadCount = response.data?.unread_count || 0;
            const previousUnreadCount = unreadCountRef.current;
            const hadBaseline = hasUnreadBaselineRef.current;

            setUnreadCount(nextUnreadCount);
            unreadCountRef.current = nextUnreadCount;
            hasUnreadBaselineRef.current = true;

            if (hadBaseline && shouldNotifyUnreadIncrease(previousUnreadCount, nextUnreadCount)) {
                const delta = nextUnreadCount - previousUnreadCount;
                if (runtimePrefs.soundEnabled && (typeof document === 'undefined' || !document.hidden)) {
                    playNotificationDing();
                }
                if (runtimePrefs.browserNotificationsEnabled) {
                    showBrowserNotification(delta);
                }
            }
        } catch (err) {
            console.error('Failed to fetch unread notification count:', err);
        }
    }, [runtimePrefs.browserNotificationsEnabled, runtimePrefs.soundEnabled, userId]);

    // Mark single notification as read
    const markAsRead = useCallback(async (notificationId) => {
        try {
            await axios.post(`/v1/notifications/${notificationId}/read`);

            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => {
                const next = Math.max(0, prev - 1);
                unreadCountRef.current = next;
                return next;
            });
        } catch (err) {
            console.error('Failed to mark as read:', err);
        }
    }, []);

    // Mark all as read
    const markAllAsRead = useCallback(async () => {
        if (!userId) return;
        try {
            await axios.post(buildMarkAllReadPath());

            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
            unreadCountRef.current = 0;
            hasUnreadBaselineRef.current = true;
            setFilter((currentFilter) => nextFilterAfterMarkAllRead(currentFilter));
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    }, [userId]);

    // Load on mount and when filter changes
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    useEffect(() => {
        if (!isWorkOrdersRoute(location.pathname)) return undefined;

        const intervalId = window.setInterval(fetchUnreadCount, WORKORDER_NOTIFICATION_POLL_MS);
        return () => window.clearInterval(intervalId);
    }, [fetchUnreadCount, location.pathname]);

    return {
        notifications,
        unreadCount,
        totalCount,
        loading,
        error,
        filter,
        setFilter,
        markAsRead,
        markAllAsRead,
        refresh: fetchNotifications,
    };
}
